import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Lock, AlertCircle, Terminal, Eye } from "lucide-react";

// Phase 36.5 — Public viewer for per-investor pitch deck magic links
// No auth required. Validates the token via /api/pitch/{token}/meta then
// renders the watermarked PDF inline in the browser.

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

export default function PitchView() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ok | revoked | expired | exhausted | not_found | error
  const [meta, setMeta] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/pitch/${token}/meta`);
        if (r.ok) {
          const data = await r.json();
          setMeta(data);
          setStatus("ok");
          return;
        }
        if (r.status === 410) {
          // either revoked or expired — backend message tells us which
          const data = await r.json().catch(() => ({}));
          const detail = (data?.detail || "").toLowerCase();
          if (detail.includes("revoked")) setStatus("revoked");
          else setStatus("expired");
          setErrorMsg(data?.detail || "");
        } else if (r.status === 429) {
          setStatus("exhausted");
        } else if (r.status === 404) {
          setStatus("not_found");
        } else {
          const data = await r.json().catch(() => ({}));
          setErrorMsg(data?.detail || "Unknown error");
          setStatus("error");
        }
      } catch (e) {
        setErrorMsg(e.message || "Network error");
        setStatus("error");
      }
    })();
  }, [token]);

  // Render
  if (status === "loading") {
    return (
      <Wrapper>
        <Loader2 className="w-8 h-8 text-[#FFB000] animate-spin" />
        <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-4">
          AUTHENTICATING LINK…
        </div>
      </Wrapper>
    );
  }

  if (status === "ok") {
    const pdfUrl = `${BACKEND}/api/pitch/${token}`;
    return (
      <div className="min-h-screen bg-black text-white" data-testid="pitch-view-ok">
        <header className="border-b border-zinc-900 py-3 px-4 sm:px-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Terminal className="w-4 h-4 text-[#FFB000]" />
            <span className="font-heading font-black text-sm tracking-tight">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              · INVESTOR DECK · CONFIDENTIAL
            </span>
          </div>
          <div className="font-mono text-[10px] text-zinc-500 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00E676]" />
              <span>{meta?.recipient_name}{meta?.recipient_company ? ` · ${meta.recipient_company}` : ""}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Eye className="w-3 h-3" />
              <span>{meta?.remaining_views} view{meta?.remaining_views === 1 ? "" : "s"} remaining</span>
            </div>
          </div>
        </header>

        <div className="p-2 sm:p-4">
          <iframe
            src={pdfUrl}
            title="DARA Investor Deck"
            data-testid="pitch-view-iframe"
            className="w-full bg-zinc-950 border border-zinc-900"
            style={{ height: "calc(100vh - 60px)" }}
          />
        </div>
      </div>
    );
  }

  // Error / blocked states
  const errors = {
    revoked: {
      icon: <Lock className="w-8 h-8 text-[#FF6B47]" />,
      title: "This link has been revoked.",
      body: "The DARA team has revoked access to this deck. If this is unexpected, please reach out to whoever shared the link with you for a fresh one.",
    },
    expired: {
      icon: <AlertCircle className="w-8 h-8 text-[#FFB000]" />,
      title: "This link has expired.",
      body: "Magic links are time-limited. Reply to whoever shared this with you and they can mint a new one in 5 seconds.",
    },
    exhausted: {
      icon: <Eye className="w-8 h-8 text-[#FFB000]" />,
      title: "View limit reached.",
      body: "This link has been opened the maximum number of times allowed. Request a fresh link from the DARA team.",
    },
    not_found: {
      icon: <AlertCircle className="w-8 h-8 text-zinc-500" />,
      title: "Link not found.",
      body: "Double-check the URL — the token may be mistyped, or the link may have been deleted.",
    },
    error: {
      icon: <AlertCircle className="w-8 h-8 text-[#FF6B47]" />,
      title: "Something went wrong.",
      body: errorMsg || "Network or server issue. Please try again in a moment.",
    },
  };
  const e = errors[status];

  return (
    <Wrapper>
      {e.icon}
      <h1 className="font-heading font-black text-2xl tracking-tight mt-5" data-testid={`pitch-view-${status}`}>
        {e.title}
      </h1>
      <p className="text-sm text-zinc-400 mt-3 max-w-md leading-relaxed">{e.body}</p>
      <Link to="/" className="mt-8 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
        ← daraos.ai
      </Link>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
      <div className="border-l-2 border-[#FFB000] pl-2 mb-8">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000]">// DARA OS</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5">INVESTOR DECK</div>
      </div>
      {children}
    </div>
  );
}
